import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createClient, localTokenStore, type Api } from './api';
import { DEFAULT_PICKUP, describePoint } from './places';
import type { LatLng, Place, Quote, Rider, RiderSettings, VehicleType } from './types';

/** What the rider is putting together before booking. Kept in sessionStorage so a reload doesn't lose it. */
export type Draft = {
  pickup: Place;
  drop: Place | null;
  quote: Quote | null;
  vehicleType: VehicleType;
  note: string;
  /** an AVAILABLE reward to take off this fare (GET /v1/rider/rewards), or null */
  rewardId?: string | null;
  /** the pickup is where the phone is (shown as "Current location") */
  pickupIsHere?: boolean;
};

const DRAFT_KEY = 'zr-draft';
/** The ride type the rider last picked, remembered across visits (Choose ride opens on it). */
const TYPE_KEY = 'zr-type';
const lastType = (): VehicleType => {
  try {
    const v = localStorage.getItem(TYPE_KEY);
    return v === 'BIKE' || v === 'AUTO' || v === 'CAR' ? v : 'AUTO';
  } catch {
    return 'AUTO';
  }
};
const defaultPickup = (): Place => ({ lat: DEFAULT_PICKUP.lat, lng: DEFAULT_PICKUP.lng, address: describePoint(DEFAULT_PICKUP).title });

function loadDraft(): Draft {
  try {
    const v = sessionStorage.getItem(DRAFT_KEY);
    if (v) return JSON.parse(v) as Draft;
  } catch {
    /* fall through */
  }
  return { pickup: defaultPickup(), drop: null, quote: null, vehicleType: lastType(), note: '' };
}

type AppCtx = {
  api: Api;
  signedIn: boolean;
  rider: Rider | null;
  setRider: (r: Rider | null) => void;
  refreshRider: () => Promise<Rider | null>;
  settings: RiderSettings | null;
  draft: Draft;
  setDraft: (patch: Partial<Draft>) => void;
  resetDraft: () => void;
  /** The phone's position when the rider allowed it. */
  here: LatLng | null;
  locate: () => Promise<LatLng | null>;
  signOut: () => Promise<void>;
  onSignedIn: (r: Rider) => void;
};

const Ctx = createContext<AppCtx | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [signedIn, setSignedIn] = useState(() => !!localTokenStore.get());
  const [rider, setRider] = useState<Rider | null>(null);
  const [settings, setSettings] = useState<RiderSettings | null>(null);
  const [draft, setDraftState] = useState<Draft>(loadDraft);
  const [here, setHere] = useState<LatLng | null>(null);

  const api = useMemo(
    () =>
      createClient(localTokenStore, undefined, () => {
        setSignedIn(false);
        setRider(null);
      }),
    [],
  );

  const refreshRider = useCallback(async () => {
    try {
      const r = await api.me();
      setRider(r);
      return r;
    } catch {
      return null;
    }
  }, [api]);

  useEffect(() => {
    if (!signedIn) return;
    void refreshRider();
    api.settings().then(setSettings, () => undefined);
  }, [signedIn, api, refreshRider]);

  const setDraft = useCallback((patch: Partial<Draft>) => {
    if (patch.vehicleType)
      try {
        localStorage.setItem(TYPE_KEY, patch.vehicleType);
      } catch {
        /* ignore */
      }
    setDraftState((d) => {
      const next = { ...d, ...patch };
      try {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const resetDraft = useCallback(() => {
    setDraftState((d) => {
      const next: Draft = { pickup: d.pickup, pickupIsHere: d.pickupIsHere, drop: null, quote: null, vehicleType: d.vehicleType, note: '', rewardId: null };
      try {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const locate = useCallback(
    () =>
      new Promise<LatLng | null>((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (p) => {
            const at = { lat: p.coords.latitude, lng: p.coords.longitude };
            setHere(at);
            resolve(at);
          },
          () => resolve(null),
          { enableHighAccuracy: true, timeout: 8000, maximumAge: 30_000 },
        );
      }),
    [],
  );

  const value: AppCtx = {
    api,
    signedIn,
    rider,
    setRider,
    refreshRider,
    settings,
    draft,
    setDraft,
    resetDraft,
    here,
    locate,
    onSignedIn: (r) => {
      setRider(r);
      setSignedIn(true);
    },
    signOut: async () => {
      await api.logout();
      setRider(null);
      setSignedIn(false);
      try {
        sessionStorage.removeItem(DRAFT_KEY);
        localStorage.removeItem('zr-finish');
      } catch {
        /* ignore */
      }
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useApp outside AppProvider');
  return c;
}
